/**
 * V13.19 — Page publique détail d'une édition des Jeux/Mini-Jeux du Pacifique.
 *
 * Route : /memoire/jeux-du-pacifique/:editionKey
 *
 * Affiche :
 *   - En-tête : nom, année, hôte, dates, source officielle, stats médailles PF
 *   - Tableau filtrable des résultats (discipline / médaille / recherche athlète)
 *   - Highlight des lignes Polynésie
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Trophy, Flag, MapPin, Calendar, ArrowLeft, ExternalLink, Search,
  Filter, Archive, AlertCircle, Medal, Users,
} from "lucide-react";

const TYPE_LABEL = {
  pacific_games: "Jeux du Pacifique",
  pacific_mini_games: "Mini-Jeux du Pacifique",
};

const MEDAL_META = {
  or:     { label: "Or",     emoji: "🥇", cls: "bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold-strong,var(--aito-gold)))] border-[hsl(var(--aito-gold))]/40" },
  argent: { label: "Argent", emoji: "🥈", cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200" },
  bronze: { label: "Bronze", emoji: "🥉", cls: "bg-amber-100 text-amber-900 border-amber-300" },
};

const ALL = "__all__";

function formatDiscipline(slug) {
  if (!slug) return "—";
  return String(slug)
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function MedalBadge({ medal, rank }) {
  const m = (medal || "").toLowerCase();
  const meta = MEDAL_META[m];
  if (meta) {
    return (
      <Badge className={`${meta.cls} font-semibold`} data-testid={`medal-${m}`}>
        <span className="mr-1">{meta.emoji}</span>
        {meta.label}
      </Badge>
    );
  }
  if (rank != null) {
    return <span className="font-mono text-xs text-muted-foreground">#{rank}</span>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

function StatKpi({ label, value, tone }) {
  const cls =
    tone === "or"
      ? "text-[hsl(var(--aito-gold))]"
      : tone === "argent"
      ? "text-slate-500 dark:text-slate-300"
      : tone === "bronze"
      ? "text-amber-700 dark:text-amber-500"
      : tone === "total"
      ? "text-[hsl(var(--aito-lagoon))]"
      : "text-foreground";
  return (
    <div className="flex flex-col" data-testid={`edition-kpi-${tone || label.toLowerCase()}`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`font-mono text-lg sm:text-xl font-bold leading-none ${cls}`}>{value}</span>
    </div>
  );
}

export default function EditionDetailPage() {
  const { editionKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [q, setQ] = useState("");
  const [disciplineFilter, setDisciplineFilter] = useState(ALL);
  const [medalFilter, setMedalFilter] = useState(ALL);
  const [pfOnly, setPfOnly] = useState(false);
  // V13.21 (Itération D) — Onglet actif du tableau des médailles
  const [medalTab, setMedalTab] = useState("general");

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/games/editions/${encodeURIComponent(editionKey)}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.status === 404 ? "Édition introuvable." : "Impossible de charger l'édition.");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [editionKey]);

  const ed = data?.edition;
  const rawResults = data?.results || [];
  const disciplines = data?.disciplines || [];
  // V13.21 (Itération D) — Tableaux médailliers + focus Tahiti calculés côté backend
  const medalTable = data?.medal_table;
  const tahitiFocus = data?.tahiti_focus;

  const filtered = useMemo(() => {
    const qNorm = q.trim().toLowerCase();
    return rawResults.filter((r) => {
      if (pfOnly && !r.is_pf) return false;
      if (disciplineFilter !== ALL && (r.discipline || "") !== disciplineFilter) return false;
      if (medalFilter !== ALL) {
        if (medalFilter === "any" && !r.medal) return false;
        if (medalFilter !== "any" && (r.medal || "").toLowerCase() !== medalFilter) return false;
      }
      if (qNorm) {
        const hay = `${r.full_name || ""} ${r.event_label || ""} ${r.delegation_code || ""}`.toLowerCase();
        if (!hay.includes(qNorm)) return false;
      }
      return true;
    });
  }, [rawResults, q, disciplineFilter, medalFilter, pfOnly]);

  const totalResults = rawResults.length;
  const visibleResults = filtered.length;

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !ed) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <h1 className="font-display text-2xl font-bold mb-2">Édition indisponible</h1>
        <p className="text-sm text-muted-foreground mb-6">{error || "Cette édition n'existe pas ou n'a pas encore été publiée."}</p>
        <Link
          to="/memoire#focus-jeux-pacifique"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
          data-testid="edition-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour aux archives des Jeux
        </Link>
      </div>
    );
  }

  const stats = ed.stats_pf || { or: 0, argent: 0, bronze: 0, total: 0 };
  const host = [ed.host_city, ed.host_country].filter(Boolean).join(", ") || "Lieu inconnu";
  const dates = [ed.start_date, ed.end_date].filter(Boolean).join(" → ");

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6" data-testid="edition-detail-page">
      {/* Fil d'Ariane */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 flex-wrap">
        <Link to="/" className="hover:underline">Accueil</Link>
        <span>/</span>
        <Link to="/memoire" className="hover:underline">Mémoire du Sport</Link>
        <span>/</span>
        <Link to="/memoire#focus-jeux-pacifique" className="hover:underline">Focus Jeux du Pacifique</Link>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{ed.name}</span>
      </nav>

      {/* En-tête édition */}
      <div className="rounded-2xl aito-grad-hero text-white p-5 sm:p-7 mb-5 overflow-hidden">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]">
                <Trophy className="h-3 w-3 mr-1" />
                {TYPE_LABEL[ed.type] || "Édition"}
              </Badge>
              <Badge className="bg-white/15 text-white border border-white/20 font-mono">
                <Calendar className="h-3 w-3 mr-1" />
                {ed.year}
              </Badge>
              {ed.status && (
                <Badge className="bg-white/15 text-white border border-white/20 uppercase text-[10px]">
                  {ed.status}
                </Badge>
              )}
            </div>
            <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold leading-tight">
              {ed.name}
            </h1>
            <div className="mt-2 flex items-center gap-4 flex-wrap text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {host}
              </span>
              {dates && (
                <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                  <Calendar className="h-3.5 w-3.5" />
                  {dates}
                </span>
              )}
            </div>
            {ed.official_source_url && (
              <a
                href={ed.official_source_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-[hsl(var(--aito-gold))] hover:text-white hover:underline"
                data-testid="edition-source-link"
              >
                <ExternalLink className="h-3 w-3" />
                Source officielle
              </a>
            )}
          </div>
        </div>
      </div>

      {/* KPI Polynésie */}
      <Card className="p-4 mb-5" data-testid="edition-pf-kpi">
        <div className="flex items-center flex-wrap gap-x-6 gap-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground shrink-0">
            <Flag className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
            Polynésie française
            {tahitiFocus?.rank_general && (
              <Badge className="ml-1 bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30" data-testid="edition-tahiti-rank">
                #{tahitiFocus.rank_general} au classement général
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-4 gap-x-6 gap-y-2 flex-1">
            <StatKpi label="Or" value={stats.or} tone="or" />
            <StatKpi label="Argent" value={stats.argent} tone="argent" />
            <StatKpi label="Bronze" value={stats.bronze} tone="bronze" />
            <StatKpi label="Total" value={stats.total} tone="total" />
          </div>
          <div className="ml-auto shrink-0 flex items-center gap-2 text-xs text-muted-foreground">
            <Archive className="h-3.5 w-3.5" />
            {totalResults.toLocaleString("fr-FR")} résultats importés
          </div>
        </div>

        {/* V13.21 (Itération D) — Focus Tahiti : disciplines médaillées + médaillés */}
        {tahitiFocus && (tahitiFocus.disciplines?.length > 0 || tahitiFocus.medalists?.length > 0) && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-3">
            {tahitiFocus.disciplines?.length > 0 && (
              <div data-testid="tahiti-focus-disciplines">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Disciplines médaillées</div>
                <div className="flex flex-wrap gap-1.5">
                  {tahitiFocus.disciplines.map((d) => (
                    <Badge key={d.discipline} variant="outline" className="text-xs bg-[hsl(var(--aito-gold))]/5 border-[hsl(var(--aito-gold))]/30">
                      <span className="font-semibold">{formatDiscipline(d.discipline)}</span>
                      <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                        {d.or > 0 && `🥇${d.or}`}{d.argent > 0 && ` 🥈${d.argent}`}{d.bronze > 0 && ` 🥉${d.bronze}`}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {tahitiFocus.medalists?.length > 0 && (
              <div data-testid="tahiti-focus-medalists">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Médaillés Polynésie ({tahitiFocus.medalists.length})</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {tahitiFocus.medalists.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[hsl(var(--aito-gold))]/5 border border-[hsl(var(--aito-gold))]/20" data-testid={`tahiti-medalist-${idx}`}>
                      <MedalBadge medal={m.medal} rank={m.rank} />
                      <div className="min-w-0 flex-1">
                        {m.athlete_id ? (
                          <Link to={`/athletes/${m.athlete_id}`} className="font-semibold text-[hsl(var(--aito-lagoon))] hover:underline">
                            {m.full_name}
                          </Link>
                        ) : m.is_team ? (
                          <span className="font-semibold inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {m.team_name || m.full_name || "Équipe"}
                          </span>
                        ) : (
                          <span className="font-semibold">{m.full_name || "—"}</span>
                        )}
                        <div className="text-[10px] text-muted-foreground truncate">
                          {formatDiscipline(m.discipline)} · {m.event_label || "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* V13.21 (Itération D) — Tableaux des médailles (3 vues) */}
      {medalTable?.general?.length > 0 && (
        <Card className="p-0 overflow-hidden mb-5" data-testid="edition-medal-table">
          <Tabs value={medalTab} onValueChange={setMedalTab}>
            <div className="px-4 pt-3 pb-2 border-b bg-slate-50/60 dark:bg-slate-900/40">
              <div className="flex items-center gap-2 mb-2">
                <Medal className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <h2 className="font-display font-semibold text-sm">Tableau des médailles</h2>
              </div>
              <TabsList className="h-8">
                <TabsTrigger value="general" className="text-xs" data-testid="medal-tab-general">Général</TabsTrigger>
                <TabsTrigger value="by-discipline" className="text-xs" data-testid="medal-tab-by-discipline">Par discipline</TabsTrigger>
                <TabsTrigger value="by-delegation" className="text-xs" data-testid="medal-tab-by-delegation">Par délégation</TabsTrigger>
              </TabsList>
            </div>

            {/* A. Général */}
            <TabsContent value="general" className="m-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white dark:bg-slate-900">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                      <th className="px-3 py-2 w-10 text-center">#</th>
                      <th className="px-3 py-2">Délégation</th>
                      <th className="px-3 py-2 text-center">🥇 Or</th>
                      <th className="px-3 py-2 text-center">🥈 Argent</th>
                      <th className="px-3 py-2 text-center">🥉 Bronze</th>
                      <th className="px-3 py-2 text-center font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medalTable.general.map((row, i) => {
                      const isPf = ["tahiti","polynésie française","french polynesia","pyf","pf","polynesie francaise"].includes((row.delegation||"").toLowerCase());
                      return (
                        <tr key={row.delegation} className={`border-b last:border-0 ${isPf ? "bg-[hsl(var(--aito-gold))]/5 font-semibold" : "hover:bg-slate-50 dark:hover:bg-slate-900/40"}`} data-testid={`medal-general-row-${i}`}>
                          <td className="px-3 py-2 text-center font-mono text-xs">{i + 1}</td>
                          <td className="px-3 py-2">
                            {isPf && <Flag className="inline h-3 w-3 mr-1 text-[hsl(var(--aito-gold))]" />}
                            {row.delegation}
                          </td>
                          <td className="px-3 py-2 text-center font-mono">{row.or}</td>
                          <td className="px-3 py-2 text-center font-mono">{row.argent}</td>
                          <td className="px-3 py-2 text-center font-mono">{row.bronze}</td>
                          <td className="px-3 py-2 text-center font-mono font-bold">{row.total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* B. Par discipline */}
            <TabsContent value="by-discipline" className="m-0">
              <div className="max-h-[420px] overflow-auto">
                {medalTable.by_discipline.map((block) => (
                  <div key={block.discipline} className="border-b last:border-0" data-testid={`medal-disc-${block.discipline}`}>
                    <h3 className="px-3 py-1.5 bg-slate-50 dark:bg-slate-900 font-display font-semibold text-xs uppercase tracking-wide sticky top-0">
                      {formatDiscipline(block.discipline)}
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {block.rows.map((row, i) => {
                          const isPf = ["tahiti","polynésie française"].includes((row.delegation||"").toLowerCase());
                          return (
                            <tr key={row.delegation} className={`border-t first:border-0 ${isPf ? "bg-[hsl(var(--aito-gold))]/5 font-semibold" : ""}`}>
                              <td className="px-3 py-1.5 text-xs text-muted-foreground w-10 text-center font-mono">{i + 1}</td>
                              <td className="px-3 py-1.5">
                                {isPf && <Flag className="inline h-3 w-3 mr-1 text-[hsl(var(--aito-gold))]" />}
                                {row.delegation}
                              </td>
                              <td className="px-3 py-1.5 text-center font-mono w-16 text-[hsl(var(--aito-gold))]">🥇{row.or}</td>
                              <td className="px-3 py-1.5 text-center font-mono w-16 text-slate-500">🥈{row.argent}</td>
                              <td className="px-3 py-1.5 text-center font-mono w-16 text-amber-700">🥉{row.bronze}</td>
                              <td className="px-3 py-1.5 text-center font-mono font-bold w-16">{row.total}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* C. Par délégation */}
            <TabsContent value="by-delegation" className="m-0">
              <div className="max-h-[420px] overflow-auto divide-y">
                {medalTable.by_delegation.map((block) => (
                  <details key={block.delegation} className="group" open={block.is_pf} data-testid={`medal-deleg-${block.delegation}`}>
                    <summary className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-900/40 ${block.is_pf ? "bg-[hsl(var(--aito-gold))]/5" : ""}`}>
                      <span className="font-display font-semibold text-sm inline-flex items-center gap-1.5">
                        {block.is_pf && <Flag className="h-3 w-3 text-[hsl(var(--aito-gold))]" />}
                        {block.delegation}
                      </span>
                      <span className="text-xs font-mono">
                        <span className="text-[hsl(var(--aito-gold))]">🥇{block.totals.or}</span>{" "}
                        <span className="text-slate-500">🥈{block.totals.argent}</span>{" "}
                        <span className="text-amber-700">🥉{block.totals.bronze}</span>{" "}
                        <span className="font-bold">= {block.totals.total}</span>
                      </span>
                    </summary>
                    <table className="w-full text-xs bg-slate-50/40 dark:bg-slate-900/20">
                      <tbody>
                        {block.disciplines.map((d) => (
                          <tr key={d.discipline} className="border-t">
                            <td className="px-6 py-1.5">{formatDiscipline(d.discipline)}</td>
                            <td className="px-3 py-1.5 text-center font-mono text-[hsl(var(--aito-gold))]">🥇{d.or}</td>
                            <td className="px-3 py-1.5 text-center font-mono text-slate-500">🥈{d.argent}</td>
                            <td className="px-3 py-1.5 text-center font-mono text-amber-700">🥉{d.bronze}</td>
                            <td className="px-3 py-1.5 text-center font-mono font-bold">{d.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      )}

      {/* Filtres */}
      <Card className="p-3 mb-4" data-testid="edition-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mr-1">
            <Filter className="h-3.5 w-3.5" />
            Filtres
          </div>

          <div className="relative min-w-[220px] flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un athlète, une épreuve, une délégation…"
              className="pl-8 h-9"
              data-testid="edition-filter-search"
            />
          </div>

          <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="edition-filter-discipline">
              <SelectValue placeholder="Discipline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes les disciplines</SelectItem>
              {disciplines.map((d) => (
                <SelectItem key={d} value={d}>{formatDiscipline(d)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={medalFilter} onValueChange={setMedalFilter}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="edition-filter-medal">
              <SelectValue placeholder="Médaille" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les rangs</SelectItem>
              <SelectItem value="any">🏅 Toutes médailles</SelectItem>
              <SelectItem value="or">🥇 Or</SelectItem>
              <SelectItem value="argent">🥈 Argent</SelectItem>
              <SelectItem value="bronze">🥉 Bronze</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant={pfOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setPfOnly((v) => !v)}
            data-testid="edition-filter-pf"
            className={pfOnly ? "bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/90" : ""}
          >
            <Flag className="h-3.5 w-3.5 mr-1" />
            Polynésie uniquement
          </Button>

          {(q || disciplineFilter !== ALL || medalFilter !== ALL || pfOnly) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQ(""); setDisciplineFilter(ALL); setMedalFilter(ALL); setPfOnly(false); }}
              data-testid="edition-filter-clear"
            >
              Effacer les filtres
            </Button>
          )}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {visibleResults.toLocaleString("fr-FR")} / {totalResults.toLocaleString("fr-FR")} résultat{totalResults > 1 ? "s" : ""} affiché{visibleResults > 1 ? "s" : ""}
        </div>
      </Card>

      {/* Tableau des résultats */}
      {totalResults === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="edition-empty">
          <Medal className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-display font-semibold text-lg mb-1">Aucun résultat publié pour cette édition</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            L'édition a été créée mais les résultats n'ont pas encore été validés par l'administration.
            Ils apparaîtront ici une fois l'import officiel finalisé.
          </p>
        </Card>
      ) : visibleResults === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="edition-no-match">
          <Search className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-display font-semibold text-lg mb-1">Aucun résultat ne correspond aux filtres</h3>
          <p className="text-sm text-muted-foreground">Essayez d'élargir votre recherche ou d'effacer les filtres.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden" data-testid="edition-results-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Discipline</th>
                  <th className="px-3 py-2 font-semibold">Épreuve</th>
                  <th className="px-3 py-2 font-semibold">Athlète / Équipage</th>
                  <th className="px-3 py-2 font-semibold">Délégation</th>
                  <th className="px-3 py-2 font-semibold text-center">Rang</th>
                  <th className="px-3 py-2 font-semibold">Médaille</th>
                  <th className="px-3 py-2 font-semibold">Performance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const rowCls = r.is_pf
                    ? "bg-[hsl(var(--aito-gold))]/5 hover:bg-[hsl(var(--aito-gold))]/10"
                    : "hover:bg-slate-50 dark:hover:bg-slate-900/40";
                  return (
                    <tr
                      key={r.id || idx}
                      className={`border-b border-slate-100 dark:border-slate-800 last:border-0 ${rowCls}`}
                      data-testid={`edition-result-row-${idx}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-medium">{formatDiscipline(r.discipline)}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.event_label || "—"}</td>
                      <td className="px-3 py-2">
                        {r.athlete_id ? (
                          <Link
                            to={`/athletes/${r.athlete_id}`}
                            className="font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
                            title="Voir la fiche AITO"
                          >
                            {r.full_name}
                          </Link>
                        ) : (
                          <span className={r.is_pf ? "font-semibold" : ""}>{r.full_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-xs ${r.is_pf ? "font-semibold text-[hsl(var(--aito-gold-strong,var(--aito-gold)))]" : "text-muted-foreground"}`}>
                          {r.is_pf && <Flag className="h-3 w-3" />}
                          {r.delegation_code || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center font-mono text-xs">
                        {r.rank != null ? `#${r.rank}` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <MedalBadge medal={r.medal} rank={r.rank} />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.performance || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Retour */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/memoire#focus-jeux-pacifique"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
          data-testid="edition-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour aux archives des Jeux
        </Link>
        <p className="text-[11px] text-muted-foreground italic">
          Les résultats des athlètes AITO alimentent automatiquement leur palmarès personnel.
        </p>
      </div>
    </div>
  );
}
